export class CumulativeSumBy {
  constructor(groupBy, aggregate, nameAggregate) {
    this.groupBy = groupBy;
    this.aggregate  = aggregate;
    this.nameAggregate = nameAggregate ? nameAggregate : aggregate;
  }

  fn = (data) => {
    let cumulativeSum = 0;
    return Array([this.groupBy, this.nameAggregate]).concat(
      Object.entries(
        data.reduce((acc, obj) => {
          cumulativeSum += Number(obj[this.aggregate]);
          acc[obj[this.groupBy]] = cumulativeSum;
          return acc;
        }, {})
      ).map((row) => {
        row[1] = {
          v: row[1],
          f: row[1].toFixed(2)
        };
        return row;
      }, [])
    );
  }
}

export class CumulativeSumByMultiple {
  constructor(groupBy, aggregate, totalColumn = true) {
    this.groupBy = groupBy[0];
    this.groupBy2 = groupBy[1];
    this.aggregate  = aggregate;
    this.totalColumn = totalColumn;
  }

  fn = (data) => {
    const groupBys = data.reduce((acc, obj) => {
      const key2 = obj[this.groupBy2];
      if (acc.indexOf(key2) === -1) {
        acc.push(key2);
      }
      return acc;
    },  []);

    const cumulativeSums = Array(groupBys.length).fill(0);

    const header = Array(this.groupBy).concat(groupBys);

    const result = data.reduce((acc, obj) => {
      const key1 = obj[this.groupBy];
      const key2 = obj[this.groupBy2];

      if (!acc[key1]) {
        acc[key1] = cumulativeSums.slice();
      }

      const key2HeaderIndex =  groupBys.indexOf(key2);
      cumulativeSums[key2HeaderIndex] += Number(obj[this.aggregate]);
      acc[key1][key2HeaderIndex] =  cumulativeSums[key2HeaderIndex];
      return acc;
    }, {});


    const isTotalColumnAdded = header.length > 2 && this.totalColumn;

    if (isTotalColumnAdded) {
      const flatData = Object.entries(result).map(row => {
        const sum = row[1].reduce((acc, value) => (acc + value), 0);
        row[1].push(sum);
        return row.slice(0,1).concat(row[1]);
      });

      header.push('Total');
      return Array(header).concat(flatData);
    }

    const flatData = Object.entries(result).map(row => {
      return row.slice(0,1).concat(row[1]);
    });

    return Array(header).concat(flatData);
  }
}

export class SumBy {
  constructor(groupBy, aggregate, nameAggregate) {
    this.groupBy = groupBy;
    this.aggregate  = aggregate;
    this.nameAggregate = nameAggregate ? nameAggregate : aggregate;
  }

  fn = (data) => (
    Array([this.groupBy, this.nameAggregate]).concat(
      Object.entries(
        data.reduce((acc, obj) => {
          const key = obj[this.groupBy];
          if (key in acc) {
            acc[key] += Number(obj[this.aggregate]);
          } else {
            acc[key] = Number(obj[this.aggregate]);
          }
          return acc;
        }, {})
      ).filter((row) => (Number(row[1]) !== 0))
        .map((row) => {
          row[1]= Number(row[1].toFixed(2));
          return row;
        }, [])
    )
  )
}

export class SumByShowOnlyAmount {
  constructor(amount, groupBy, aggregate, nameGroupBy, nameAggregate) {
    this.amount = amount;
    this.groupBy = groupBy;
    this.aggregate  = aggregate;
    this.nameGroupBy = nameGroupBy ? nameGroupBy : groupBy;
    this.nameAggregate = nameAggregate ? nameAggregate : aggregate;
  }

  fn = (data) => (
    Array([this.nameGroupBy, this.nameAggregate]).concat(
      Object.entries(
        data.reduce((acc, obj) => {
          const key = obj[this.groupBy];
          if (key in acc) {
            acc[key] += Number(obj[this.aggregate]);
          } else {
            acc[key] = Number(obj[this.aggregate]);
          }
          return acc;
        }, {})
      ).filter((row) => (Number(row[1]) !== 0))
        .map((row) => {
          row[1] = Number(row[1].toFixed(2));
          return row;
        }, [])
        .sort((a, b) => {
          return b[1] - a[1];
        })
        .slice(0,this.amount)
    )
  )
}

export class SumByMultiple {
  constructor(groupBy, aggregate) {
    this.groupBy = groupBy[0];
    this.groupBy2 = groupBy[1];
    this.aggregate  = aggregate;
  }

  fn = (data) => {
    const groupBys = data.reduce((acc, obj) => {
      const key2 = obj[this.groupBy2];
      if (acc.indexOf(key2) === -1) {
        acc.push(key2);
      }
      return acc;
    },  []);

    const header = Array(this.groupBy).concat(groupBys);

    const result = data.reduce((acc, obj) => {
      const key1 = obj[this.groupBy];
      const key2 = obj[this.groupBy2];

      if (!acc[key1]) {
        acc[key1] = Array(groupBys.length).fill(0);
      }

      const key2HeaderIndex =  groupBys.indexOf(key2);
      acc[key1][key2HeaderIndex] += Number(obj[this.aggregate]);
      return acc;
    }, {});

    const flatData = Object.entries(result).map(row => {
      return row.slice(0,1).concat(row[1]);
    });

    return Array(header).concat(flatData);
  }
}

export class SumUniqueBy {
  constructor(groupBy, unique, aggregate, nameAggregate) {
    this.groupBy = groupBy;
    this.unique = unique;
    this.aggregate  = aggregate;
    this.nameAggregate = nameAggregate ? nameAggregate : aggregate;
  }

  fn = (data) => (
    Array([this.groupBy, this.nameAggregate]).concat(
      Object.entries(
        data.reduce((acc, obj) => {
          const key = obj[this.groupBy];
          if (key in acc) {
            if (acc[key].values.indexOf(obj[this.unique]) === -1) {
              acc[key].values.push(obj[this.unique]);
              acc[key].total += Number(obj[this.aggregate]);
            }
          } else {
            acc[key] = {
              values: Array.of(obj[this.unique]),
              total: Number(obj[this.aggregate])
            };

          }
          return acc;
        }, {})
      ).map((row) => {
        row[1]= Number(row[1].total.toFixed(2));
        return row;
      }, [])
    )
  )
}

export class AvgByUnique {
  constructor(groupBy, unique, aggregate, nameAggregate) {
    this.groupBy = groupBy;
    this.unique = unique;
    this.aggregate  = aggregate;
    this.nameAggregate = nameAggregate ? nameAggregate : aggregate;
  }

  fn = (data) => (
    Array([this.groupBy, this.nameAggregate]).concat(
      Object.entries(
        data.reduce((acc, obj) => {
          const key = obj[this.groupBy];
          if (key in acc) {
            if (obj[this.unique] in acc[key]) {
              acc[key][obj[this.unique]] += Number(obj[this.aggregate]);
            } else {
              acc[key][obj[this.unique]] = Number(obj[this.aggregate]);
            }
          } else {
            acc[key] = {};
            acc[key][obj[this.unique]] = Number(obj[this.aggregate]);
          }
          return acc;
        }, {})
      ).map((row) => {
        const values = Object.values(row[1]);

        const avg = values
          .map(value => Number(value.toFixed(2)))
          .reduce((acc, next) => acc + next, 0)  / values.length;

        row[1] = Number(avg);
        return row;
      }, [])
    )
  )
}

export class RatioBetweenTwoValues {
  constructor(groupBy, numerator, denominator) {
    this.groupBy = groupBy;
    this.numerator  = numerator;
    this.denominator = denominator;
  }

  fn = (data) => (
    Array([this.groupBy, this.numerator + '/' + this.denominator]).concat(
      Object.entries(
        data.reduce((acc, obj) => {
          const key = obj[this.groupBy];
          if (key in acc) {
            acc[key][this.numerator] += Number(obj[this.numerator]);
            acc[key][this.denominator] += Number(obj[this.denominator]);
          } else {
            acc[key] = {};
            acc[key][this.numerator] = Number(obj[this.numerator]);
            acc[key][this.denominator] = Number(obj[this.denominator]);
          }
          return acc;
        }, {})
      ).map((row) => {
        row[1] = row[1][this.numerator] / (row[1][this.denominator] === 0 ? 1 : row[1][this.denominator]) * 100;
        return row;
      }, [])
    )
  )
}

export class ProportionTwoValues {
  constructor(groupBy, numerator, denominator, nameNumerator, nameDenominator) {
    this.groupBy = groupBy;
    this.numerator  = numerator;
    this.denominator = denominator;
    this.nameNumerator = nameNumerator;
    this.nameDenominator = nameDenominator;
  }

  fn = (data) => (
    Array([this.groupBy, this.nameDenominator, this.nameNumerator]).concat(
      Object.entries(
        data.reduce((acc, obj) => {
          const key = obj[this.groupBy];
          if (obj[this.denominator] === 0 && obj[this.numerator] === 0) {
            return acc;
          }
          if (key in acc) {
            acc[key][this.numerator] += Number(obj[this.numerator]);
            acc[key][this.denominator] += Number(obj[this.denominator]);
          } else {
            acc[key] = {};
            acc[key][this.numerator] = Number(obj[this.numerator]);
            acc[key][this.denominator] = Number(obj[this.denominator]);
          }
          return acc;
        }, {})
      ).filter((row) => (row[1][this.numerator] > 0)).map((row) => {
        row[1] = row[1][this.denominator] / ((row[1][this.numerator] + row[1][this.denominator]) === 0 ? 1 : (row[1][this.numerator] + row[1][this.denominator])) * 100;
        row[2] = 100 - row[1];
        return row;
      }, [])
    )
  )
}



// WEBPACK FOOTER //
// ./src/Dashboard/Functions.js